// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Every overlay in this tool goes through here, so the dismissal contract is
 * written once and cannot be forgotten by a later surface.
 *
 * FOUR exits, all of them, always:
 *   1. an × control of at least 44×44 CSS px, inside the panel;
 *   2. a pointerdown anywhere outside the panel;
 *   3. Escape, from anywhere on the page;
 *   4. the backdrop is a real hit area (position:fixed; inset:0), below the
 *      panel and above the page, never pointer-events:none.
 *
 * `pointerdown` rather than `click` because it covers mouse, touch and pen in
 * one handler and fires where a synthetic click would not — and because a
 * control wired to BOTH `click` and `touchstart` double-fires on a real tap,
 * closing and instantly reopening, which reads on a phone as a dead button.
 * One event family per control, everywhere in this file.
 *
 * The backdrop is removed from the DOM entirely on close rather than hidden,
 * so there is no path by which a stale transparent overlay can survive to
 * swallow taps.
 */

let openCount = 0;
let lastFocused: HTMLElement | null = null;

export interface OpenModal {
  close: () => void;
  panel: HTMLElement;
  body: HTMLElement;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function openModal(title: string, bodyHtml: string, labelledBy = 'modal-title'): OpenModal {
  const host = document.getElementById('modal-host');
  if (!host) throw new Error('modal host missing');

  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';

  const panel = document.createElement('div');
  panel.className = 'modal-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-labelledby', labelledBy);

  const head = document.createElement('div');
  head.className = 'modal-head';

  const heading = document.createElement('h2');
  heading.className = 'modal-title';
  heading.id = labelledBy;
  heading.textContent = title;

  const closeBtn = document.createElement('button');
  closeBtn.className = 'icon-btn';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', `Close ${title}`);
  closeBtn.innerHTML =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  head.append(heading, closeBtn);

  const body = document.createElement('div');
  body.className = 'modal-body';
  body.innerHTML = bodyHtml;

  panel.append(head, body);
  backdrop.append(panel);

  let closed = false;

  const close = () => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey, true);
    backdrop.remove();
    openCount = Math.max(0, openCount - 1);
    if (openCount === 0) {
      document.body.style.removeProperty('overflow');
      lastFocused?.focus?.();
      lastFocused = null;
    }
  };

  function onKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    // Keep focus inside the dialog, so the page behind it is never reachable
    // by keyboard while the overlay is up.
    const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === closeBtn,
    );
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Exit 2 + 4: anything outside the panel. Listening on the backdrop rather
  // than the document means a pointerdown that began inside the panel and
  // drifted out (selecting text) cannot close it.
  backdrop.addEventListener('pointerdown', (event) => {
    if (!panel.contains(event.target as Node)) close();
  });

  // Exit 1.
  closeBtn.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    close();
  });
  // Keyboard activation of the × does not produce a pointerdown.
  closeBtn.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      close();
    }
  });

  // Exit 3.
  document.addEventListener('keydown', onKey, true);

  if (openCount === 0) {
    lastFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
  }
  openCount += 1;

  host.append(backdrop);
  closeBtn.focus();

  return { close, panel, body };
}

/**
 * A modal that asks before doing something, with a named action.
 *
 * Used once, for the thing this tool most needs to ask about: recovering an
 * earlier version of a document can undo a redaction, and the person whose
 * account number is under the black box is not the person clicking the button.
 * It still ships the full four exits — refusing is always one Escape away.
 */
export function confirmModal(
  title: string,
  bodyHtml: string,
  confirmLabel: string,
  onConfirm: () => void,
): OpenModal {
  const modal = openModal(title, bodyHtml, 'confirm-title');

  const row = document.createElement('div');
  row.className = 'action-row modal-actions';

  const go = document.createElement('button');
  go.className = 'btn btn-primary';
  go.type = 'button';
  go.textContent = confirmLabel;
  go.addEventListener('click', () => {
    modal.close();
    onConfirm();
  });

  const cancel = document.createElement('button');
  cancel.className = 'btn';
  cancel.type = 'button';
  cancel.textContent = 'Not now';
  cancel.addEventListener('click', () => modal.close());

  row.append(go, cancel);
  modal.body.append(row);
  go.focus();
  return modal;
}
